import {
    Button,
    Card,
    CardHeader,
    FluentProvider,
    makeStyles,
    tokens,
    typographyStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import WindowsAppIcon from '../../../content/images/Icon_WindowsApp.svg';
import { useCurrentFluent2Theme } from '../../../hooks/styling';

interface ConnectionCardProps {
    onActionInvoked: () => void;
    onWindowsAppLinkInvoked?: () => void;
    onDismiss?: () => void;
}

const messages = defineMessages({
    closeButtonAriaLabel: {
        id: 'QuickActionCard_CloseButton_AriaLabel',
        defaultMessage: 'Close',
        description: 'Aria label for quick action card "Close" button',
    },
});

/**
 * Styles
 */

const useCardStyles = makeStyles({
    root: {
        width: '305px',
    },
    icon: {
        height: '32px',
    },
    title: typographyStyles.body1Strong,
    content: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
    },
});

/**
 * END Styles
 */

export const ConfigureConnectionCard: React.FC<ConnectionCardProps> = React.memo((props: ConnectionCardProps) => {
    const { onDismiss, onWindowsAppLinkInvoked } = props;

    // Intl hooks
    const { formatMessage } = useIntl();

    const theme = useCurrentFluent2Theme();

    // Style hooks
    const cardStyles = useCardStyles();

    // Callback hooks
    const onDismissHandler = React.useCallback(
        (
            event: React.MouseEvent<
                HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
                MouseEvent
            >
        ) => {
            if (onDismiss) {
                // Block event from propagating up to the card's click handler so that you don't also open the dialog when you attempt to dismiss the card
                event.stopPropagation();

                onDismiss();
            }
        },
        [onDismiss]
    );

    return (
        <FluentProvider theme={theme}>
            <Card onClick={onWindowsAppLinkInvoked} focusMode="off" className={cardStyles.root}>
                <CardHeader
                    image={<img src={WindowsAppIcon} className={cardStyles.icon} alt="" />}
                    header={
                        <div className={cardStyles.title}>
                            <FormattedMessage
                                id="DevBoxes_DownloadWindowsAppActionTitle_Text"
                                defaultMessage="Download Windows App"
                                description="Title of the 'Download Windows App' quick action card"
                            />
                        </div>
                    }
                    action={
                        <Button
                            appearance="subtle"
                            aria-label={formatMessage(messages.closeButtonAriaLabel)}
                            icon={<Dismiss24Regular />}
                            onClick={onDismissHandler}
                        />
                    }
                />
                <div className={cardStyles.content}>
                    <FormattedMessage
                        id="DevBoxes_DownloadWindowsAppActionSubtitle_Text"
                        defaultMessage="Install the Windows App for the best performance when connecting to your dev boxes."
                        description="Explanatory text for the 'Download Windows App' quick action card"
                    />
                </div>
            </Card>
        </FluentProvider>
    );
});

export default ConfigureConnectionCard;
